/**
 * Turns a Unix timestamp in seconds into a Date, preserving null.
 *
 * The provisioning API serializes dates as seconds, not milliseconds — passing the raw value to the
 * Date constructor yields January 1970.
 *
 * @param {number|null|undefined} seconds The timestamp in seconds, or nothing.
 *
 * @returns {Date|null} The date, or null when there is none.
 */
export function secondsToDateOrNull( seconds ) {
	return seconds === null || seconds === undefined ? null : new Date( seconds * 1000 );
}

/**
 * Maps a `SubscriptionProvisioningResponseDto` (GET :id and every mutation) onto the shape the components
 * read: lowercase `id` and real Date objects.
 *
 * @param {Object} dto The subscription as the API returns it.
 *
 * @returns {Object} The subscription as the components expect it.
 */
export function toProvisionedSubscription( dto ) {
	return {
		id: dto.ID,
		subscriptionNumber: dto.subscriptionNumber,
		customerEmail: dto.customerEmail,
		product: dto.product,
		status: dto.status,
		startDate: secondsToDateOrNull( dto.startDate ),
		endDate: secondsToDateOrNull( dto.endDate ),
		pluginDownloadUrls: dto.pluginDownloadUrls ?? [],
		siteUrl: dto.siteUrl,
	};
}

/**
 * Maps a `ProvisionedSubscriptionListItemDto` onto a table row: the same shape as a detail subscription
 * minus the download URLs, which the list endpoint does not carry.
 *
 * @param {Object} dto The list item as the API returns it.
 *
 * @returns {Object} The row as the table expects it.
 */
export function toProvisionedSubscriptionListItem( dto ) {
	return {
		...dto,
		startDate: secondsToDateOrNull( dto.startDate ),
		endDate: secondsToDateOrNull( dto.endDate ),
	};
}

/**
 * Formats a product as "Name (code)", degrading to whichever part is known.
 *
 * @param {{code: string|null, name: string|null}|null} product The product.
 *
 * @returns {string} The label, or a dash when neither part is known.
 */
export function productLabel( product ) {
	if ( ! product ) {
		return "—";
	}
	if ( product.name && product.code ) {
		return `${ product.name } (${ product.code })`;
	}
	return product.name || product.code || "—";
}

/** Statuses that end a subscription for good: nothing can be done with one any more. */
const FINAL_STATUSES = [ "cancelled", "refunded" ];

/**
 * Whether a provisioned subscription can still be renewed, cancelled or refunded. Connecting a site has
 * its own rule: `canSetSite`.
 *
 * @param {Object} subscription The subscription.
 *
 * @returns {boolean} Whether actions apply.
 */
export function isActionable( subscription ) {
	return ! FINAL_STATUSES.includes( String( subscription.status ?? "" ).toLowerCase() );
}

/** Statuses the server accepts a site change for; it refuses one for any other status. */
const SITE_STATUSES = [ "active", "pending-cancel" ];

/**
 * Whether a site can be connected to a provisioned subscription.
 *
 * @param {Object} subscription The subscription.
 *
 * @returns {boolean} Whether connecting a site applies.
 */
export function canSetSite( subscription ) {
	return SITE_STATUSES.includes( String( subscription.status ?? "" ).toLowerCase() );
}

/** Statuses the provisioning routes answer with a message of their own; others carry framework defaults. */
const SERVER_MESSAGE_STATUSES = [ 400, 403 ];

/**
 * The reason the server gave for a failed provisioning request.
 *
 * Provisioners are trusted API consumers, so the server's own message is shown as is. NestJS answers a
 * validation failure with a list of messages. A throttled request gets the copy `customFetch` gave it.
 *
 * @param {Object|null} error The mutation error.
 *
 * @returns {string|null} The message, or null when the server gave none.
 */
export function serverErrorMessage( error ) {
	const status = error?.response?.status;
	if ( status === 429 ) {
		return error.message;
	}
	if ( ! SERVER_MESSAGE_STATUSES.includes( status ) ) {
		return null;
	}

	const message = error.body?.message;
	if ( Array.isArray( message ) ) {
		return message.join( " " ) || null;
	}
	return typeof message === "string" && message ? message : null;
}
